import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@core/store/hooks';
import { useActiveOrganization } from '../hooks';
import { setActiveOrganization } from '../store/organizationSlice';

/**
 * Active-organization control for the app header.
 *
 * - Nothing to show (no session, list not loaded) → renders nothing.
 * - A single organization → a static label, because there is nothing to choose.
 * - Several → a self-contained dropdown that dispatches `setActiveOrganization`,
 *   which re-scopes every subsequent request through the `X-Organization` header.
 *
 * Accessible (`role="menu"` / `menuitemradio`, `aria-expanded`, Escape and arrow
 * navigation, click-outside), with >=40px tap targets, and it truncates rather
 * than overflowing down to 375px.
 */
export function OrganizationSwitcher() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { uuid, organization, organizations, canSwitch, hasNoSelectableOrganization } =
    useActiveOrganization();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Close on outside click.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Where focus lands when the menu opens: on the active item for a keyboard user,
  // on the menu itself after a click, so nothing lights up as if it had been chosen
  // (the same rule as the `Dropdown` primitive).
  const openedByKeyboard = useRef(false);
  useEffect(() => {
    if (!open) {
      return;
    }
    if (!openedByKeyboard.current) {
      menuRef.current?.focus();
      return;
    }
    const activeIndex = Math.max(
      0,
      organizations.findIndex((candidate) => candidate.uuid === uuid),
    );
    itemRefs.current[activeIndex]?.focus();
  }, [open, organizations, uuid]);

  const activeName = organization?.name ?? '';

  // Every organization the caller has was refused (403). Say so — silently rendering
  // nothing is how the old clear/re-select loop stayed invisible.
  if (!activeName && hasNoSelectableOrganization) {
    return (
      <p role="status" className="min-w-0 truncate text-sm text-[var(--color-danger)]">
        {t('organization.switcher.loadError')}
      </p>
    );
  }

  if (!activeName) {
    return null; // nothing to show until the active organization is known
  }

  // A single organization → a plain static label (no dropdown to open).
  if (!canSwitch) {
    return (
      <div className="flex min-w-0 items-center gap-x-2">
        <Icon
          icon="heroicons:building-office-2"
          className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]"
          aria-hidden="true"
        />
        {/* A visually-hidden label, not aria-label: aria-label on a role-less
            generic element is ignored by assistive technology. */}
        <span className="sr-only">{t('organization.switcher.label')}</span>
        {/* On a phone the header has no room for the name next to the company and the
            user: the icon stands for it and the label above says what it is. */}
        <span className="hidden max-w-[200px] truncate text-sm font-medium text-[var(--color-text)] sm:inline">
          {activeName}
        </span>
      </div>
    );
  }

  const handleSelect = (organizationUuid: string, byKeyboard: boolean) => {
    if (organizationUuid !== uuid) {
      dispatch(setActiveOrganization(organizationUuid));
    }
    setOpen(false);
    if (byKeyboard) {
      triggerRef.current?.focus();
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    const index = items.indexOf(document.activeElement as HTMLButtonElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      items[(index + 1) % items.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      items[index < 0 ? items.length - 1 : (index - 1 + items.length) % items.length]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      items[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      items[items.length - 1]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div ref={containerRef} className="relative inline-flex min-w-0">
      <button
        ref={triggerRef}
        type="button"
        id="organization-switcher-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('organization.switcher.change')}
        onClick={(event) => {
          openedByKeyboard.current = event.detail === 0;
          setOpen((value) => !value);
        }}
        className="inline-flex min-h-[40px] min-w-0 cursor-pointer items-center gap-x-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 text-sm font-medium text-[var(--color-text)] transition-colors hover:border-[var(--color-border-strong)] focus:outline-hidden focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-1"
      >
        <Icon
          icon="heroicons:building-office-2"
          className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]"
          aria-hidden="true"
        />
        <span className="max-w-[30vw] truncate sm:max-w-[180px]">{activeName}</span>
        <Icon
          icon="heroicons:chevron-up-down"
          className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]"
          aria-hidden="true"
        />
      </button>

      {open && (
        // Mobile: viewport-anchored under the top bar (never overflows the screen
        // edge). Desktop: anchored to the trigger's right edge.
        // `top-[calc(4rem+env(safe-area-inset-top))]` matches the header's real
        // height: AppLayout wraps an h-16 row in `pt-[env(safe-area-inset-top)]`, so
        // a flat top-16 would sit over the trigger on a notched device.
        <div
          ref={menuRef}
          className="fixed inset-x-4 top-[calc(4rem+env(safe-area-inset-top))] z-50 w-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-1.5 shadow-sm focus:outline-hidden sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-64"
          role="menu"
          aria-labelledby="organization-switcher-trigger"
          tabIndex={-1}
          onKeyDown={onMenuKeyDown}
        >
          {/* role="none" on both wrappers: a generic element between role="menu" and
              its role="menuitemradio" children breaks the required owned-element
              relationship, so set size/position may not be announced at all. */}
          <div role="none" className="mb-1 border-b border-[var(--color-border)] px-2.5 py-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
              {t('organization.switcher.label')}
            </p>
          </div>

          <div role="none" className="max-h-72 overflow-y-auto">
            {organizations.map((candidate, index) => {
              const isActive = candidate.uuid === uuid;
              return (
                <button
                  key={candidate.uuid}
                  ref={(element) => {
                    itemRefs.current[index] = element;
                  }}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={(event) => handleSelect(candidate.uuid, event.detail === 0)}
                  className={[
                    'flex min-h-[40px] w-full cursor-pointer items-center gap-x-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]',
                    isActive
                      ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
                      : 'text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]',
                  ].join(' ')}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">{candidate.name}</span>
                  {isActive && (
                    <Icon icon="heroicons:check" className="h-4 w-4 shrink-0" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
